/**
 * NotificationsSection — in-app notification preferences.
 *
 * Stored as `settings` rows with category='notifications'.
 * Keys used:
 *   NOTIFY_LOW_STOCK          'true' | 'false'
 *   NOTIFY_SYNC_ERRORS        'true' | 'false'
 *   NOTIFY_DAY_CLOSE_REMINDER 'true' | 'false'
 *   NOTIFY_SUPPLIER_OVERDUE   'true' | 'false'
 *   NOTIFY_BACKUP_STATUS      'true' | 'false'
 */

import { notifySuccess } from '@/store/useToastStore';
import { Switch } from '@/components/ui/Switch';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import { Setting } from '@/db/models/System';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useAppStore } from '@/store/useAppStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const PREF_ITEMS: { key: string; label: string; description: string }[] = [
  {
    key: 'NOTIFY_LOW_STOCK',
    label: 'Low stock alerts',
    description: 'Notify when a product falls below its reorder level.',
  },
  {
    key: 'NOTIFY_SYNC_ERRORS',
    label: 'Sync errors',
    description: 'Notify when data cannot be synchronised with the server.',
  },
  {
    key: 'NOTIFY_DAY_CLOSE_REMINDER',
    label: 'Day-close reminder',
    description: 'Remind you at end of day to close the session and verify the cash drawer.',
  },
  {
    key: 'NOTIFY_SUPPLIER_OVERDUE',
    label: 'Supplier payment overdue',
    description: "Alert when a supplier's credit period has lapsed.",
  },
  {
    key: 'NOTIFY_BACKUP_STATUS',
    label: 'Backup status',
    description: 'Notify when an automatic backup completes or fails.',
  },
];

export function NotificationsSection() {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const { getSetting, setSettingCache } = useSettingsStore();

  const [prefs, setPrefs] = useState<Record<string, boolean>>(
    Object.fromEntries(PREF_ITEMS.map((p) => [p.key, getSetting(p.key) !== 'false'])),
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  function toggle(key: string) {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function save() {
    if (!merchantId) return;
    setSaving(true);
    setError(null);
    try {
      await txn(async (db) => {
        for (const [key, val] of Object.entries(prefs)) {
          const stringVal = val ? 'true' : 'false';
          const existing = await database
            .get<Setting>('settings')
            .query(Q.where('merchant_id', merchantId), Q.where('setting_key', key))
            .fetch();
          if (existing.length > 0) {
            await existing[0].update((s) => { s.settingValue = stringVal; s.isSynced = false; });
          } else {
            await db.get<Setting>('settings').create((s) => {
              s.merchantId = merchantId;
              s.storeId = null;
              s.category = 'notifications';
              s.settingKey = key;
              s.settingValue = stringVal;
              s.version = 1;
              s.isSynced = false;
              s.isDeleted = false;
            });
          }
          setSettingCache(key, stringVal);
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      notifySuccess('Notification preferences saved.');
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-heading font-semibold text-fg-primary">Notification Preferences</h2>
        <p className="mt-1 text-body text-fg-secondary">
          Choose which in-app alerts you want to receive on this device.
        </p>
      </div>

      <Card className="p-5 divide-y divide-stroke-hairline/10">
        {PREF_ITEMS.map(({ key, label, description }) => (
          <div key={key} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <div className="text-body font-medium text-fg-primary">{label}</div>
              <div className="mt-0.5 text-label text-fg-secondary">{description}</div>
            </div>
            <Switch checked={prefs[key]} onChange={() => toggle(key)} label={label} />
          </div>
        ))}
      </Card>


      <div className="flex items-center gap-3">
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </div>
  );
}
